// Melody as stored on the chart (the score model): note onsets and durations
// are INTEGER TICKS (48 per beat), not fractional beats, so triplets, dotted
// figures and swung subdivisions round-trip exactly through JSON persistence —
// a triplet eighth is 16 ticks, never 0.3333….
//
// The notation engine and melodyLead keep consuming the fractional-beat
// MelodyNote (song.ts); melodyFromTicks/melodyToTicks convert at that boundary.

import {
  accidentalForKey,
  pitchClassOf,
  spellNote,
  type Accidental,
  type KeyContext,
} from './notes';
import { uid, type MelodyNote } from './song';

/** Ticks per quarter-beat unit. 48 = exact eighths, sixteenths and triplets. */
export const TICKS_PER_BEAT = 48;

/**
 * A pitched, rhythmic note in a measure's melody voice — the chart-native
 * sibling of MelodyNote, with tick timing. `startTicks` is measured from the
 * barline (beat 1 = tick 0; MelodyNote.beat is 1-based).
 */
export interface ChartMelodyNote {
  id: string;
  /** Absolute pitch as MIDI number (C4 = middle C = 60). Ignored for rests. */
  midi: number;
  /**
   * Optional explicit spelling ("Eb" vs "D#"). Spelling decides the staff
   * line, so it matters for engraving; when omitted, spell from the key.
   */
  spelledAs?: string;
  startTicks: number;
  durationTicks: number;
  tie?: 'start' | 'stop' | 'both';
  /** A rest occupies time but draws no notehead. */
  rest?: boolean;
}

/** Convert a fractional-beat melody note (song model) to tick storage. */
export function melodyNoteToTicks(n: MelodyNote): ChartMelodyNote {
  return {
    id: n.id,
    midi: n.midi,
    ...(n.spelledAs ? { spelledAs: n.spelledAs } : {}),
    startTicks: Math.round((n.beat - 1) * TICKS_PER_BEAT),
    durationTicks: Math.round(n.duration * TICKS_PER_BEAT),
    ...(n.tie ? { tie: n.tie } : {}),
    ...(n.rest ? { rest: true } : {}),
  };
}

/** Convert a stored tick note back to the fractional-beat song model. */
export function melodyNoteFromTicks(n: ChartMelodyNote): MelodyNote {
  return {
    id: n.id,
    midi: n.midi,
    ...(n.spelledAs ? { spelledAs: n.spelledAs } : {}),
    beat: 1 + n.startTicks / TICKS_PER_BEAT,
    duration: n.durationTicks / TICKS_PER_BEAT,
    ...(n.tie ? { tie: n.tie } : {}),
    ...(n.rest ? { rest: true } : {}),
  };
}

export function melodyToTicks(notes: MelodyNote[]): ChartMelodyNote[] {
  return notes.map(melodyNoteToTicks);
}

export function melodyFromTicks(notes: ChartMelodyNote[]): MelodyNote[] {
  return notes.map(melodyNoteFromTicks);
}

/** Copy a melody with fresh note ids (the cloneChords analogue). */
export function cloneMelody(notes: ChartMelodyNote[]): ChartMelodyNote[] {
  return notes.map((n) => ({ ...n, id: uid('n') }));
}

/**
 * Transpose a melody by a number of semitones, register-preserving: the chart
 * transposer normalizes the shift to 0..11 (pitch classes don't care), but on
 * MIDI a +11 would fling the line up most of an octave — so the shift goes the
 * NEAREST way (+11 ≡ −1). An explicit spelling keeps its accidental side
 * (flat stays flat, sharp stays sharp); naturals defer to the destination key.
 */
export function transposeMelody(
  notes: ChartMelodyNote[],
  semitones: number,
  key: KeyContext | null,
): ChartMelodyNote[] {
  const shift = ((semitones % 12) + 12) % 12;
  const midiShift = shift > 6 ? shift - 12 : shift;
  return notes.map((n) => {
    if (n.rest || shift === 0) return { ...n };
    const midi = Math.min(127, Math.max(0, n.midi + midiShift));
    const out: ChartMelodyNote = { ...n, midi };
    if (n.spelledAs) out.spelledAs = respell(n.spelledAs, shift, key);
    return out;
  });
}

/**
 * A tie-in whose start is missing (the note began before this span) is a
 * rest. Looping a mid-phrase bar must not re-strike a held note; the time
 * stays, the attack does not. Ties that begin inside the span are unchanged.
 */
export function restIncomingTies<T extends { melody?: ChartMelodyNote[] }>(
  measures: readonly T[],
): T[] {
  const openInSlice = new Set<number>();
  return measures.map((measure) => {
    if (!measure.melody?.length) return measure;
    let changed = false;
    const melody = measure.melody.map((note) => {
      if (note.rest) return note;
      const incoming = note.tie === 'stop' || note.tie === 'both';
      const outgoing = note.tie === 'start' || note.tie === 'both';
      if (incoming && !openInSlice.has(note.midi)) {
        changed = true;
        const silenced = { ...note, rest: true };
        delete silenced.tie;
        return silenced;
      }
      if (outgoing) openInSlice.add(note.midi);
      else openInSlice.delete(note.midi);
      return note;
    });
    return changed ? { ...measure, melody } : measure;
  });
}

function respell(spelling: string, shift: number, key: KeyContext | null): string {
  const acc: Accidental = spelling.includes('b')
    ? 'flat'
    : spelling.includes('#')
      ? 'sharp'
      : accidentalForKey(key);
  return spellNote((pitchClassOf(spelling) + shift) % 12, acc);
}
